/**
 * /legal/cookies — Cookie policy with category table.
 */

import { createFileRoute } from '@tanstack/react-router';
import { LegalLayout } from '../../components/layouts/legal-layout';

export const Route = createFileRoute('/legal/cookies')({
  component: CookiesPage,
});

function CookiesPage() {
  return (
    <LegalLayout heading="Cookie policy." lastUpdated="7 May 2026">
      <p>
        Elreign uses a small number of cookies and browser storage mechanisms to operate the
        platform securely. We do not use cookies for advertising or behavioural tracking.
        The table below explains what we use and why.
      </p>

      <h2>What is a cookie?</h2>
      <p>
        A cookie is a small text file stored in your browser when you visit a website. Cookies
        allow the website to recognise your browser on subsequent visits and to maintain state
        across pages (for example, keeping you signed in).
      </p>
      <p>
        We also use <strong>localStorage</strong> for certain preferences (for example,
        subdomain routing state) that do not require a server round-trip.
      </p>

      <h2>Categories we use</h2>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Category</th>
            <th>Used</th>
            <th>Purpose</th>
            <th>Can be disabled?</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Strictly necessary</strong></td>
            <td>Yes</td>
            <td>
              Session token (keeps you signed in), CSRF protection token, and the immutable
              audit-chain session identifier required to attribute actions in the trust record
              to an authenticated user.
            </td>
            <td>No, the platform cannot function without these.</td>
          </tr>
          <tr>
            <td><strong>Functional</strong></td>
            <td>Yes</td>
            <td>
              Tenant subdomain routing (remembers which firm&apos;s portal you access so you are
              not redirected to the wrong login page), and theme persistence (light mode is the
              only current mode; reserved for a future dark-mode toggle).
            </td>
            <td>Yes, but loss of functionality (you will need to re-enter your firm&apos;s portal address each visit).</td>
          </tr>
          <tr>
            <td><strong>Analytics</strong></td>
            <td>No</td>
            <td>We do not use behaviour analytics cookies. No page-view tracking, heatmaps, or session recordings are active.</td>
            <td>Not applicable.</td>
          </tr>
          <tr>
            <td><strong>Marketing</strong></td>
            <td>No</td>
            <td>We do not use marketing or advertising cookies. No retargeting pixels or advertising networks are present on any Elreign page.</td>
            <td>Not applicable.</td>
          </tr>
        </tbody>
      </table>

      <h2>Cookie lifetimes</h2>
      <p>
        Session cookies are deleted when you close your browser. Persistent session tokens
        (used to keep you signed in across browser restarts) expire after 30 days. Functional
        localStorage items have no expiry but can be cleared by clearing your browser&apos;s site data.
      </p>

      <h2>How to manage cookies</h2>
      <p>
        You can manage cookies through your browser settings. Deleting strictly necessary cookies
        will sign you out of the platform. Your browser&apos;s help documentation explains how to
        manage cookies for:
      </p>
      <ul>
        <li>
          <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)' }}>
            Google Chrome
          </a>
        </li>
        <li>
          <a href="https://support.mozilla.org/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)' }}>
            Mozilla Firefox
          </a>
        </li>
        <li>
          <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)' }}>
            Apple Safari
          </a>
        </li>
        <li>
          <a href="https://support.microsoft.com/windows/delete-and-manage-cookies" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)' }}>
            Microsoft Edge
          </a>
        </li>
      </ul>

      <h2>Changes to this policy</h2>
      <p>
        We will update this policy if we introduce new cookies. Material changes will be noted
        at the top of this page with a revised date.
      </p>
    </LegalLayout>
  );
}
